import { Box, useTheme } from '@mui/material';
import React, { PropsWithChildren, useCallback, useEffect, useRef, useState } from 'react';
import { SwipeEventData, useSwipeable } from 'react-swipeable';

import type { Theme } from '../../../../themes/themes';
import { getStyles } from './ScrollBar.styles';

let capture = false;
let currentX = 0;

type ScrollBarProps = PropsWithChildren<{
  scrollIntoView?: HTMLElement;
  onScrolled?(data: SwipeEventData): void;
}>;

export const ScrollBar: React.FC<ScrollBarProps> = ({ children, scrollIntoView, onScrolled = () => undefined }) => {
  const theme = useTheme<Theme>();
  const styles = getStyles({ theme });
  const [translateX, setTranslateX] = useState<number>(0);
  const [withAnimation, setWithAnimation] = useState<boolean>(false);
  const [pointerEvents, setPointerEvents] = useState<boolean>(false);
  const innerRef = useRef(null);
  const outerRef = useRef(null);

  const innerStyles = Object.assign({}, styles.inner, withAnimation && styles.innerAnimated);
  const outerStyles = Object.assign({}, styles.outer, pointerEvents && styles.disablePointerEevents);

  const updateXPosition = (value: number) => {
    const innerElement = innerRef.current.getBoundingClientRect();
    const outerElement = outerRef.current.getBoundingClientRect();

    if (value > 0) {
      value = 0;
    }

    const absX = Math.abs(value);
    let maxSize = innerElement.width - outerElement.width;
    maxSize = maxSize < 0 ? 0 : maxSize;

    if (absX > maxSize) {
      value = maxSize * -1;
    }

    setTranslateX(value);
  };

  useEffect(() => {
    if (scrollIntoView) {
      const scrollBar = outerRef.current.getBoundingClientRect();
      const scrollToElement = scrollIntoView.getBoundingClientRect();
      const scrollTo =
        translateX * -1 + (scrollToElement.left - scrollBar.left + scrollToElement.width / 2 - scrollBar.width / 2);
      updateXPosition(scrollTo * -1);
      setWithAnimation(true);
    }
  }, [scrollIntoView]);

  useEffect(() => {
    const innerElement = innerRef.current;

    const handleTransition = () => {
      setWithAnimation(false);
    };

    innerElement.addEventListener('transitionend', handleTransition);
    return () => {
      innerElement.removeEventListener('transitionend', handleTransition);
    };
  }, []);

  const handleSwiping = ({ deltaX }) => {
    const innerElement = innerRef.current.getBoundingClientRect();
    const outerElement = outerRef.current.getBoundingClientRect();

    if (outerElement.width > innerElement.width) {
      return;
    }

    if (!capture) {
      currentX = translateX;
      capture = true;
    }

    updateXPosition(currentX + deltaX);
  };

  const handlers = useSwipeable({
    trackMouse: true,
    preventScrollOnSwipe: true,
    onSwiping: handleSwiping,
    onSwiped: (props: SwipeEventData) => {
      onScrolled(props);
      capture = false;
    },
    onSwipeStart: () => {
      setPointerEvents(true);
    },
    onTouchEndOrOnMouseUp: () => {
      setPointerEvents(false);
    },
  });

  const outerRefPassthrough = useCallback((element) => {
    handlers.ref(element);
    outerRef.current = element;
  }, []);

  const handleDragStart = useCallback((event) => event.preventDefault(), []);

  return (
    <Box sx={outerStyles} {...handlers} ref={outerRefPassthrough} onDragStart={handleDragStart}>
      <Box ref={innerRef} sx={innerStyles} style={{ transform: `translateX(${translateX}px)` }}>
        {children}
      </Box>
    </Box>
  );
};
